/* eslint-disable react/static-property-placement */
// copy paste from https://github.com/mbrn/material-table/blob/b26e2f715abb2217f0d14e1efb15c6367fe12de1/src/components/m-table-header.js#L17
// + added some class names
// + width patch
// + changed tooltip component
// + added word break & font styles
// + added data-sorting
// + added ellipsis and sort icon opacity styles

/* eslint-disable no-unused-vars */
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import {withStyles} from '@mui/styles';
import classNames from 'classnames';
import * as CommonValues from 'material-table/dist/utils/common-values';
import PropTypes from 'prop-types';
import * as React from 'react';
import {Draggable} from 'react-beautiful-dnd';

import {JustTooltipFormatter} from './formatters';
import {AG_GRID_COMPATIBILITY} from './LightTable';

export function columnWidthPatch(columnDef: any) {
  return {
    ...columnDef,
    tableData: {...columnDef.tableData, width: columnDef.width ?? 'none'},
  };
  // return { ...columnDef, maxWidth: columnDef.width };
}

/* eslint-enable no-unused-vars */
/* eslint-disable react/destructuring-assignment, no-nested-ternary, react/forbid-prop-types */
type MTableHeaderState = {
  lastX: number;
  resizingColumnDef: any;
  lastAdditionalWidth?: number;
};
export class MTableHeaderWrapper extends React.Component<any, MTableHeaderState> {
  constructor(props) {
    super(props);

    this.state = {
      lastX: 0,
      resizingColumnDef: undefined,
    };
  }

  // shouldComponentUpdate(nextProps, nextState){
  //   return !equal(nextProps, this.props) || !equal(nextState, this.state);
  // }

  componentDidMount() {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  handleMouseDown = (e, columnDef) => {
    this.setState({
      lastAdditionalWidth: columnDef.tableData.additionalWidth,
      lastX: e.clientX,
      resizingColumnDef: columnDef,
    });
  };

  handleMouseMove = (e) => {
    if (!this.state.resizingColumnDef) {
      return;
    }

    let additionalWidth = this.state.lastAdditionalWidth + e.clientX - this.state.lastX;

    additionalWidth = Math.min(this.state.resizingColumnDef.maxWidth || additionalWidth, additionalWidth);

    if (this.state.resizingColumnDef.tableData.additionalWidth !== additionalWidth) {
      this.props.onColumnResized(this.state.resizingColumnDef.tableData.id, additionalWidth);
    }
  };

  handleMouseUp = (e) => {
    this.setState({resizingColumnDef: undefined});
  };

  getCellStyle = (_columnDef) => {
    const columnDef = columnWidthPatch(_columnDef);
    const {width} = columnDef.tableData;

    const style = {
      ...this.props.headerStyle,
      ...columnDef.headerStyle,
      boxSizing: 'border-box',
      width,
      maxWidth: columnDef.maxWidth,
      minWidth: columnDef.minWidth,
    };

    if (this.props.options.tableLayout === 'fixed' && this.props.options.columnResizable && columnDef.resizable !== false) {
      style.paddingRight = 2;
    }

    return style;
  };

  renderHeader() {
    const size = this.props.options.padding === 'default' ? 'medium' : 'small';
    const textStyle: React.CSSProperties = {
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      fontFamily: 'var(--dlFontFamilySemiBold)',
      letterSpacing: 'var(--dlFontLetterSpacingNormal)',
      fontSize: 'var(--dlFontSizeNormal)',
      color: 'var(--dlAgGridHeaderFontColor)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };

    const mapArr = this.props.columns
      .filter((columnDef) => !columnDef.hidden && !(columnDef.tableData.groupOrder > -1))
      .sort((a, b) => a.tableData.columnOrder - b.tableData.columnOrder)
      .map((columnDef, index) => {
        const isColumnSortable = columnDef.sorting !== false && this.props.sorting;
        let content = <div style={textStyle}>{columnDef.title}</div>;
        if (!isColumnSortable) {
          content = <div className={this.props.classes.nonSortableContent}>{content}</div>;
        }

        if (this.props.draggable) {
          content = (
            <Draggable key={columnDef.tableData.id} draggableId={columnDef.tableData.id.toString()} index={index}>
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} style={textStyle}>
                  {columnDef.title}
                </div>
              )}
            </Draggable>
          );
          if (!isColumnSortable) {
            content = <div className={this.props.classes.nonSortableContent}>{content}</div>;
          }
        }

        if (isColumnSortable) {
          const isActiveSorting = this.props.orderBy === columnDef.tableData.id;
          // PDM-780 / PDM-2727: direction applied on the first click of an inactive column (and the next one in
          // the toggle cycle). Defaults to 'asc' across all tables; a table's own default-sorted column keeps its
          // initial direction via the sort query. Tables may still opt into descending-first via firstSortDirection='desc'.
          const firstSortDirection = this.props.firstSortDirection === 'desc' ? 'desc' : 'asc';
          const secondSortDirection = firstSortDirection === 'asc' ? 'desc' : 'asc';

          content = (
            <TableSortLabel
              IconComponent={this.props.icons.SortArrow}
              active={isActiveSorting}
              // inactive columns preview the direction the first click will apply, so the hover arrow matches the result
              direction={isActiveSorting ? this.props.orderDirection || firstSortDirection : firstSortDirection}
              onClick={() => {
                const current = this.props.orderDirection;
                let orderDirection;
                if (columnDef.tableData.id !== this.props.orderBy) {
                  orderDirection = firstSortDirection;
                } else if (current === firstSortDirection) {
                  orderDirection = secondSortDirection;
                } else if (current === secondSortDirection) {
                  orderDirection = this.props.thirdSortClick ? '' : firstSortDirection;
                } else {
                  orderDirection = firstSortDirection;
                }
                this.props.onOrderChange(columnDef.tableData.id, orderDirection);
              }}
              className={classNames(this.props.classes.sortLabel, {
                [this.props.classes.sortLabelActive]: isActiveSorting,
                [this.props.classes.sortLabelInactive]: !isActiveSorting,
              })}
            >
              {content}
            </TableSortLabel>
          );
        }

        content = (
          <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={content} tooltipOverride={columnDef.tooltip ?? columnDef.title} />
        );

        if (this.props.options.tableLayout === 'fixed' && this.props.options.columnResizable && columnDef.resizable !== false) {
          content = (
            <div style={{display: 'flex', alignItems: 'center'}}>
              <div style={{flex: 1, minWidth: 0}}>{content}</div>
              <div />
              <this.props.icons.Resize
                style={{
                  cursor: 'col-resize',
                  color:
                    this.state.resizingColumnDef && this.state.resizingColumnDef.tableData.id === columnDef.tableData.id
                      ? this.props.theme.palette.primary.main
                      : 'inherit',
                }}
                onMouseDown={(e) => this.handleMouseDown(e, columnDef)}
              />
            </div>
          );
        }

        const cellAlignment =
          columnDef.align !== undefined ? columnDef.align : ['numeric', 'currency'].indexOf(columnDef.type) !== -1 ? 'right' : 'left';
        return (
          <TableCell
            data-sorting={columnDef.sorting}
            role='columnheader'
            col-id={columnDef.field}
            key={columnDef.tableData.id}
            align={cellAlignment}
            className={classNames(this.props.classes.header, 'ag-header-cell')}
            style={this.getCellStyle(columnDef)}
            size={size}
          >
            {content}
          </TableCell>
        );
      });
    return mapArr;
  }

  renderActionsHeader() {
    const localization = {
      ...(MTableHeaderWrapper as any).defaultProps.localization,
      ...this.props.localization,
    };
    const width = CommonValues.actionsColumnWidth(this.props);
    return (
      <TableCell
        key='key-actions-column'
        padding='checkbox'
        className={this.props.classes.header}
        style={{
          ...this.props.headerStyle,
          width,
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        <TableSortLabel hideSortIcon disabled>
          {localization.actions}
        </TableSortLabel>
      </TableCell>
    );
  }

  renderSelectionHeader() {
    const selectionWidth = CommonValues.selectionMaxWidth(this.props, this.props.treeDataMaxLevel);

    return (
      <TableCell
        padding='none'
        key='key-selection-column'
        className={this.props.classes.header}
        style={{
          ...this.props.headerStyle,
          width: selectionWidth,
        }}
      >
        {this.props.showSelectAllCheckbox && (
          <Checkbox
            indeterminate={this.props.selectedCount > 0 && this.props.selectedCount < this.props.dataCount}
            checked={this.props.dataCount > 0 && this.props.selectedCount === this.props.dataCount}
            onChange={(event, checked) => this.props.onAllSelected && this.props.onAllSelected(checked)}
            {...this.props.options.headerSelectionProps}
          />
        )}
      </TableCell>
    );
  }

  renderDetailPanelColumnCell() {
    return (
      <TableCell padding='none' key='key-detail-panel-column' className={this.props.classes.header} style={{...this.props.headerStyle}} />
    );
  }

  render() {
    const headers = this.renderHeader();
    if (this.props.hasSelection) {
      headers.splice(0, 0, this.renderSelectionHeader());
    }

    if (this.props.showActionsColumn) {
      if (this.props.actionsHeaderIndex >= 0) {
        let endPos = 0;
        if (this.props.hasSelection) {
          endPos = 1;
        }
        headers.splice(this.props.actionsHeaderIndex + endPos, 0, this.renderActionsHeader());
      } else if (this.props.actionsHeaderIndex === -1) {
        headers.push(this.renderActionsHeader());
      }
    }

    if (this.props.hasDetailPanel) {
      if (this.props.detailPanelColumnAlignment === 'right') {
        headers.push(this.renderDetailPanelColumnCell());
      } else {
        headers.splice(0, 0, this.renderDetailPanelColumnCell());
      }
    }

    if (this.props.isTreeData > 0) {
      headers.splice(
        0,
        0,
        <TableCell padding='none' key={'key-tree-data-header'} className={this.props.classes.header} style={{...this.props.headerStyle}} />
      );
    }

    this.props.columns
      .filter((columnDef) => columnDef.tableData.groupOrder > -1)
      .forEach((columnDef) => {
        headers.splice(
          0,
          0,
          <TableCell padding='checkbox' key={`key-group-header${columnDef.tableData.id}`} className={this.props.classes.header} />
        );
      });

    return (
      <TableHead>
        <TableRow>{headers}</TableRow>
      </TableHead>
    );
  }
}
MTableHeaderWrapper.displayName = 'MTableHeaderWrapper';

MTableHeaderWrapper.defaultProps = {
  dataCount: 0,
  hasSelection: false,
  headerStyle: {},
  selectedCount: 0,
  sorting: true,
  localization: {
    actions: 'Actions',
  },
  orderBy: undefined,
  orderDirection: 'asc',
  actionsHeaderIndex: 0,
  detailPanelColumnAlignment: 'left',
  draggable: true,
  thirdSortClick: true,
  firstSortDirection: 'asc',
};

MTableHeaderWrapper.propTypes = {
  columns: PropTypes.array.isRequired,
  dataCount: PropTypes.number,
  hasDetailPanel: PropTypes.bool.isRequired,
  detailPanelColumnAlignment: PropTypes.string,
  hasSelection: PropTypes.bool,
  headerStyle: PropTypes.object,
  localization: PropTypes.object,
  selectedCount: PropTypes.number,
  sorting: PropTypes.bool,
  onAllSelected: PropTypes.func,
  onOrderChange: PropTypes.func,
  orderBy: PropTypes.number,
  orderDirection: PropTypes.string,
  controlledSort: PropTypes.any,
  actionsHeaderIndex: PropTypes.number,
  showActionsColumn: PropTypes.bool,
  showSelectAllCheckbox: PropTypes.bool,
  draggable: PropTypes.bool,
  thirdSortClick: PropTypes.bool,
  firstSortDirection: PropTypes.oneOf(['asc', 'desc']),
  tooltip: PropTypes.string,
};

export const styles = (theme) => ({
  header: {
    // display: 'inline-block',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: theme.palette.background.paper, // Change according to theme,
  },
  sortLabel: {
    '& .MuiTableSortLabel-icon': {
      transition: 'opacity 0.2s ease-in-out',
    },
  },
  sortLabelInactive: {
    '& .MuiTableSortLabel-icon': {
      opacity: 0,
    },
    '&:hover .MuiTableSortLabel-icon': {
      opacity: 0.5,
    },
  },
  sortLabelActive: {
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
    },
  },
  nonSortableContent: {
    display: 'flex !important',
    alignItems: 'center',
    height: '100%',
    minHeight: 0,
  },
});

export default withStyles(styles, {withTheme: true})(MTableHeaderWrapper);
